import {
  Box,
  Button,
  Card,
  Container,
  Heading,
  Stack,
  Text,
} from '@chakra-ui/react';
import { FcGoogle } from 'react-icons/fc';
import { useNavigate } from 'react-router-dom';
import { signIn, signInWithRedirect, getCurrentUser, confirmSignIn, fetchAuthSession } from 'aws-amplify/auth';
import { useState, useEffect } from 'react';
import { Logo } from './Logo';

export function Login() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  useEffect(() => {
    async function checkAuthState() {
      try {
        await getCurrentUser();
        navigate('/');
      } catch {
        // User is not authenticated
      }
    }
    checkAuthState();
  }, [navigate]);

  const signInWithEmail = async () => {
    try {
      setError(null);
      // Validate inputs first
      if (!isValidEmail(email)) {
        setEmailError('Enter a valid email address');
        return;
      } else {
        setEmailError(null);
      }
      if (!password) {
        setPasswordError('Password is required');
        return;
      } else {
        setPasswordError(null);
      }
      setIsLoading(true);
      const result = await signIn({ username: email, password });
      const step = (result as unknown as { nextStep?: { signInStep?: string } })?.nextStep?.signInStep;
      if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') { 
        setChallenge('CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED');
        setIsLoading(false);
        return;
      }
      // Treat all other outcomes as success (no MFA enabled)
      await fetchAuthSession();
      navigate('/');
    } catch (error) {
      console.error("Error signing in:", error);
      setError("Invalid email or password. Please try again.");
      setIsLoading(false);
    }
  };

  const handleConfirmNewPassword = async () => {
    try {
      if (!newPassword || newPassword.length < 8) {
        setNewPasswordError('Password must be at least 8 characters');
        return;
      } else {
        setNewPasswordError(null);
      }
      setIsLoading(true);
      await confirmSignIn({ challengeResponse: newPassword });
      await fetchAuthSession();
      navigate('/');
    } catch (e) {
      console.error('Error confirming new password:', e);
      setError('Failed to set new password.');
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setIsLoading(true);
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      console.error('Error signing in:', error);
      setError("Error signing in with Google. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="md" py={{ base: '12', md: '24' }}>
      <Card.Root w="full" bg="bg.surface">
        <Card.Body>
          <Stack gap="8" align="center">
            {/* Header */}
            <Stack gap="6" align="center">
              <Box bg="white" p="2" borderRadius="md">
                <Logo />
              </Box>
              <Stack gap="3" align="center">
                <Heading size="lg">Sign in</Heading>
                <Text color="fg.muted">Gateway Logfile Tool</Text>
              </Stack>
            </Stack>

            {/* Sign in Options */}
            <Stack gap="6" w="full">
              {challenge !== 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' ? (
                <>
                  <Box>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => setEmailError(isValidEmail(email) ? null : 'Enter a valid email address')}
                      required
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #E2E8F0'
                      }}
                    />
                    {emailError && (
                      <Text color="red.500" fontSize="sm" mt={1}>{emailError}</Text>
                    )}
                  </Box>
                  <Box style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') signInWithEmail(); }}
                      required
                      style={{
                        width: '100%',
                        padding: '8px 40px 8px 8px',
                        borderRadius: '4px',
                        border: '1px solid #E2E8F0'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '12px'
                      }}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                    {passwordError && (
                      <Text color="red.500" fontSize="sm" mt={1}>{passwordError}</Text>
                    )}
                  </Box>
                  <Button
                    onClick={signInWithEmail}
                    colorScheme="blue"
                    w="full"
                    disabled={isLoading || !email || !password || !!emailError || !!passwordError}
                  >
                    {isLoading ? 'Signing in...' : 'Sign in with Email'}
                  </Button>

                  {error && (
                    <Text color="red.500" textAlign="center">
                      {error}
                    </Text>
                  )}

                  <Text textAlign="center" color="fg.muted">OR</Text>

                  {/* Google Sign in */}
                  <Button
                    onClick={signInWithGoogle}
                    variant="outline"
                    w="full"
                  >
                    {isLoading ? "Signing in..." : (
                      <>
                        <FcGoogle /> Continue with Google
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Box style={{ position: 'relative' }}>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Set a new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmNewPassword(); }}
                      required
                      minLength={8}
                      style={{
                        width: '100%',
                        padding: '8px 40px 8px 8px',
                        borderRadius: '4px',
                        border: '1px solid #E2E8F0'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((s) => !s)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '12px'
                      }}
                    >
                      {showNewPassword ? 'Hide' : 'Show'}
                    </button>
                    {newPasswordError && (
                      <Text color="red.500" fontSize="sm" mt={1}>{newPasswordError}</Text>
                    )}
                  </Box>
                  <Button
                    onClick={handleConfirmNewPassword}
                    colorScheme="blue"
                    w="full"
                    disabled={isLoading || !newPassword || !!newPasswordError}
                  >
                    {isLoading ? 'Saving...' : 'Set new password and sign in'}
                  </Button>
                </>
              )}
            </Stack>

            {/* Footer */}
            <Box as="footer">
              <Stack gap="4" align="center">
                <Text fontSize="sm" color="fg.muted">
                  © {new Date().getFullYear()} Kalmar. All rights reserved.
                </Text>
              </Stack>
            </Box>
          </Stack>
        </Card.Body>
      </Card.Root>
    </Container>
  );
}